import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import {clsx} from 'clsx';
import {add, isEqual} from 'lodash';
import React, {
  memo,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import useResizeObserver from 'use-resize-observer';

import {type DispatchableAction} from '../../../types/redux';
import {
  TABLE_MIN_COLUMN_WIDTH,
  TABLE_ROW_HEIGHT,
} from '../../../util/constants';
import {useRecordWorkspaceLoading} from '../../../util/datadog/workspace/workspaceLoading';
import {type ValueOp} from '../../../util/filterTypes';
import {type Sort as QuerySort} from '../../../util/queryTypes';
import {
  type Config as RunFeedDataConfig,
  type ConfigV2,
} from '../../../util/runfeed';
import {keyToCss} from '../../../util/runs';
import {
  type Key as RunKey,
  type Value as RunValue,
} from '../../../util/runTypes';
import {getColumnWidth} from '../../../util/wbtree';
import {type TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {useRunSelectorResizingContext} from '../../MultiRunWorkspace/RunSelectorResizingContext';
import type {RunSelectorRunsPartialVars} from '../types';
import {HorizontalRule} from './HorizontalRule';
import {
  type RowForWBTableRow,
  type WBTableColumn,
  type WBTableHoverCellCoords,
} from './types';
import {WBTableColumnHeaders} from './WBTableColumnHeaders';
import {getExpandedDescendantRowAddresses, WBTableRow} from './WBTableRow';

interface WBTableGridProps {
  addFilter(key: RunKey, op: ValueOp, value: RunValue): void;
  addGroup(newGroup: RunKey): void;
  childrenPaginationMap: {
    [key: string]: number | undefined;
  };
  className?: string;
  columnDragAccessor?: string;
  columnDropAccessor?: string;
  columnResizingAccessor?: string;
  columns: WBTableColumn[];
  expandable?: boolean;
  expanded?: boolean;
  expandedRowAddresses: string[];
  fixedColumns: string[];
  hideColumn(columnAccessor: string): void;
  hoverCellCoords: WBTableHoverCellCoords;
  isSingleMode?: boolean;
  leftMargin: number;
  loadingTable: boolean; // from parent
  moveColumn(): void;
  openSortPopup?(): void;
  pinned?: boolean;
  pinnedRows: RowForWBTableRow[];
  readOnly?: boolean;
  rows: RowForWBTableRow[];
  scrollXBounds: [number, number];
  setChildrenPagination(rowAddress: string, activePage?: number): void;
  setHoverCellCoords(newCoords: WBTableHoverCellCoords): void;
  setTableSettings(config: Partial<RunFeedDataConfig>): void;
  setTableState(stateUpdate: {
    columnDragAccessor?: string;
    columnDropAccessor?: string;
    columnResizingAccessor?: string;
    columnResizeOffset?: number;
  }): void;
  shouldSkipChildQuery?: boolean;
  showBannerRowSpacer?: boolean;
  showLogCounts?: boolean;
  showArtifactCounts?: boolean;
  sort: QuerySort;
  tableSettings: RunFeedDataConfig;
  toggleExpandedRow(rowAddress: string): void; // expand or collapse this row (add/remove rowAddress from expandedRowAddresses)
  togglePinnedColumn(columnAccessor: string): void; // add column to (or remove from) pinnedColumnKeys
  topLevelQueryVariables: RunSelectorRunsPartialVars;
  treeRef?: React.RefObject<HTMLDivElement | null>;
  truncationType: TruncationType;
  updateSort(updateFn: (sort: QuerySort) => void): void;
  updateTruncationType(truncationType: TruncationType): DispatchableAction;
  // Whether the top-level query fetched grouped rows via the lean
  // runSelectorGroups query; child queries for expanded groups follow suit.
  useLeanGroupedRunsQuery?: boolean;
}

function getMaximumColumnWidth(
  column: WBTableColumn,
  columns: WBTableColumn[],
  tableSettings: ConfigV2,
  tableWidth: number
) {
  const columnWidth = getColumnWidth(tableSettings, column.key);
  const allColumnWidths = columns
    .map(col => getColumnWidth(tableSettings, col.key))
    .reduce(add, 0);

  // Maximum column width is 90% of the table width, minus the width of all
  // the other pinned columns. This prevents a pinned column from overflowing
  // the table when the runs table is on a report.
  return tableWidth * 0.9 - allColumnWidths + columnWidth;
}

export interface VisibleColumns {
  leftPadding: number;
  rightPadding: number;
  visibleColumnRange: [number, number];
}

export const getVisibleColumns = (
  scrollXBounds: [number, number],
  columns: WBTableColumn[],
  tableSettings: ConfigV2
): VisibleColumns => {
  const actualWidths: number[] = Array(columns.length);
  let totalWidth = 0;
  columns.forEach((column, columnIndex) => {
    const columnWidth = getColumnWidth(tableSettings, column.key);
    actualWidths[columnIndex] = columnWidth;
    totalWidth += columnWidth;
  });

  let [minX, maxX] = scrollXBounds;
  const span = maxX - minX;

  const windowingBuffer = 500;
  let currentX = 0;
  let minColumn = columns.length;
  let maxColumn = 0;
  let leftPadding = 0;
  let rightPadding = 0;

  if (minX > totalWidth - span - windowingBuffer) {
    minX = totalWidth - span - windowingBuffer;
    maxX = totalWidth + windowingBuffer;
  }

  columns.forEach((_col, columnIndex) => {
    const columnWidth = actualWidths[columnIndex];
    const colLeftEdge = currentX;
    const colRightEdge = currentX + columnWidth;
    if (colRightEdge >= minX - windowingBuffer) {
      // column is visible because right edge is within buffer
      minColumn = Math.min(minColumn, columnIndex);
    } else {
      leftPadding += columnWidth;
    }
    if (colLeftEdge <= maxX + windowingBuffer) {
      // column is visible because left edge is within buffer
      maxColumn = Math.max(maxColumn, columnIndex + 1);
    } else {
      rightPadding += columnWidth;
    }
    currentX += columnWidth;
  });

  return {
    leftPadding,
    rightPadding,
    visibleColumnRange: [minColumn, maxColumn],
  };
};

const WBTableGridComp: React.FC<WBTableGridProps> = ({
  addFilter,
  childrenPaginationMap,
  className = '',
  columnDragAccessor,
  columnDropAccessor,
  columns,
  expandable,
  expanded,
  expandedRowAddresses,
  fixedColumns,
  hoverCellCoords,
  isSingleMode,
  leftMargin,
  loadingTable,
  moveColumn,
  pinned,
  pinnedRows,
  readOnly,
  rows,
  scrollXBounds,
  setChildrenPagination,
  setHoverCellCoords,
  setTableSettings,
  setTableState,
  shouldSkipChildQuery,
  showBannerRowSpacer,
  showArtifactCounts,
  showLogCounts,
  sort,
  tableSettings,
  toggleExpandedRow,
  topLevelQueryVariables,
  treeRef,
  truncationType,
  useLeanGroupedRunsQuery,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const {
    isResizing: isRunSelectorResizing,
    columnResizingAccessor,
    setColumnResizingAccessor,
  } = useRunSelectorResizingContext();

  const {width: tableWidth} = useResizeObserver({
    ref: wrapperRef as RefObject<HTMLElement>,
  });

  const trickPanelsIntoResizing = useCallback(() => {
    // panels adapt to window resizes,
    // so just mock a resize
    window.dispatchEvent(new Event('resize'));
  }, []);

  useLayoutEffect(() => {
    trickPanelsIntoResizing();
  }, [trickPanelsIntoResizing]);

  const visibleColumns = useMemo(
    () => getVisibleColumns(scrollXBounds, columns, tableSettings),
    [columns, scrollXBounds, tableSettings]
  );
  const [minColumn, maxColumn] = visibleColumns.visibleColumnRange;
  const columnCount = maxColumn - minColumn;

  const columnsSlice = useMemo(
    () => columns.slice(minColumn, maxColumn),
    [columns, maxColumn, minColumn]
  );

  let columnHeight =
    treeRef && treeRef.current && !isNaN(treeRef.current.clientHeight)
      ? treeRef.current.clientHeight - TABLE_ROW_HEIGHT
      : 0;
  if (expandable) {
    // adjust for 52px of padding used to
    // prevent pagination controls from blocking text
    columnHeight -= 52;
  }

  const renderRow = (r: RowForWBTableRow) => {
    const expandedDescendantRowAddresses = getExpandedDescendantRowAddresses(
      expandedRowAddresses,
      r.__address__
    );
    return (
      <WBTableRow
        key={r.__address__}
        addFilter={addFilter}
        childrenPaginationMap={childrenPaginationMap}
        columns={columnsSlice}
        expanded={expanded}
        expandedDescendantRowAddresses={expandedDescendantRowAddresses}
        hoverCellCoords={hoverCellCoords}
        isGroupExpanded={expandedRowAddresses.includes(r.__address__)}
        shouldSkipChildQuery={shouldSkipChildQuery}
        isSingleMode={isSingleMode}
        pinned={pinned}
        recursionDepth={0}
        row={r}
        setChildrenPagination={setChildrenPagination}
        setHoverCellCoords={setHoverCellCoords}
        showArtifactCounts={showArtifactCounts}
        showLogCounts={showLogCounts}
        toggleExpandedRow={toggleExpandedRow}
        topLevelQueryVariables={topLevelQueryVariables}
        truncationType={truncationType}
        useLeanGroupedRunsQuery={useLeanGroupedRunsQuery}
      />
    );
  };

  useRecordWorkspaceLoading('workspace_run_selector_loaded', {
    when: !loadingTable,
  });

  return (
    <div
      ref={wrapperRef}
      className={`wb-tree--${pinned ? 'pinned-' : ''}wrapper`}>
      {!pinned && (
        // This crazy thing was the only way I could get the
        // header background to be full width and sticky
        // when the grid isn't full width.
        // Let me know if you find a better way.
        <div
          className="wb-tree-header-background-wrapper"
          style={{
            pointerEvents: 'none',
            position: 'absolute',
            left: 0,
            right: 0,
            height: columnHeight,
            marginBottom: -columnHeight,
          }}>
          <div className="wb-tree-header-background" />
        </div>
      )}
      <div
        ref={treeRef as RefObject<HTMLDivElement>}
        className={clsx('wb-tree', className, {
          'wb-tree--pinned': pinned,
          'wb-tree--loading': loadingTable,
          'border-dashed': isRunSelectorResizing,
        })}
        style={{
          gridTemplateColumns: `[start] ${visibleColumns.leftPadding}px repeat(${columnCount}, fit-content(${TABLE_MIN_COLUMN_WIDTH}px)) ${visibleColumns.rightPadding}px auto [end]`,
          marginLeft: leftMargin,
        }}>
        {pinned && <div className="wb-tree-header-background" />}

        <WBTableColumnHeaders
          displayedRows={pinnedRows.concat(rows)}
          columns={columnsSlice}
          childProps={(column, columnIndex) => {
            const columnAccessor: string = column.accessor;
            const actualColumnIndex = columnIndex + minColumn;

            return {
              column,
              draggable: fixedColumns.indexOf(column.accessor) === -1,
              cellHoverProps: {
                onMouseEnter: () => {
                  setHoverCellCoords([undefined, keyToCss(column.key)]);
                },
                onMouseLeave: () => {
                  setHoverCellCoords([]);
                },
              },
              columnIndex: actualColumnIndex,
              columnMovingProps: {
                dragging: columnDragAccessor === columnAccessor,
                dropping: columnDropAccessor === columnAccessor,
                dragHandleProps: {
                  onMouseDown: () =>
                    setTableState({columnDragAccessor: columnAccessor}),
                  onMouseUp: () =>
                    setTableState({columnDragAccessor: undefined}),
                },
                onDragEnter: () => {
                  setTableState({columnDropAccessor: columnAccessor});
                },
                onDragEnd: () => {
                  setTableState({
                    columnDragAccessor: undefined,
                    columnDropAccessor: undefined,
                  });
                },
                onDrop: () => {
                  if (columnDragAccessor) {
                    moveColumn();
                  }
                },
              },
              columnResizingProps: {
                resizing: columnResizingAccessor === columnAccessor,
                onResizeStart: () => {
                  setColumnResizingAccessor(columnAccessor);
                },
                onResize: offset => {
                  setTableState({columnResizeOffset: offset});
                },
                resizeColumn: (newWidth: number) => {
                  setColumnResizingAccessor(undefined);
                  setTableState({columnResizeOffset: 0});
                  setTableSettings({
                    columnWidths: {
                      ...tableSettings.columnWidths,
                      [columnAccessor]: newWidth,
                    },
                  });
                  trickPanelsIntoResizing();
                },
              },
              columnWidth: getColumnWidth(tableSettings, column.key),
              hovering:
                columnResizingAccessor == null &&
                hoverCellCoords &&
                hoverCellCoords[1] === keyToCss(column.key),
              maxWidth:
                pinned && expanded !== false
                  ? getMaximumColumnWidth(
                      column,
                      columns,
                      tableSettings,
                      tableWidth ?? 0
                    )
                  : Infinity,
              readOnly,
              sort,
            };
          }}
        />
        <div className="wb-tree-padding" />
        {columnsSlice.map((column, columnIndex) => {
          // This section renders the background for each column, using the
          // grid as a guide for placement.
          const columnAccessor = column.accessor;
          const keyCss = keyToCss(column.key);
          const hovering =
            columnResizingAccessor == null &&
            hoverCellCoords &&
            hoverCellCoords[1] === keyCss &&
            keyCss !== 'run_name';
          const resizing = columnResizingAccessor === columnAccessor;
          const dropping = columnDropAccessor === columnAccessor;
          const cls = clsx('wb-tree-cell-column-background', {
            'wb-tree-cell--resizing': resizing,
            'wb-tree-cell--dropping': dropping,
            'wb-tree-cell--hovering': hovering,
          });
          return (
            <div
              style={{
                height: Math.max(columnHeight, 0),
                marginBottom: Math.min(-columnHeight, 0),
              }}
              // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
              key={`background-${columnIndex}`}
              className={cls}
            />
          );
        })}
        <div className="wb-tree-padding" />
        <div className="wb-tree-padding" />
        {showBannerRowSpacer ? (
          <div className="wb-table-grid-banner-cell" />
        ) : null}
        {pinnedRows ? pinnedRows.map(renderRow) : null}
        {pinnedRows?.length && rows.length ? <HorizontalRule /> : null}
        {rows.map(renderRow)}
      </div>
    </div>
  );
};

export const WBTableGrid = memo(WBTableGridComp, (prevProps, nextProps) => {
  if (
    !isEqual(
      getVisibleColumns(
        prevProps.scrollXBounds,
        prevProps.columns,
        prevProps.tableSettings
      ),
      getVisibleColumns(
        nextProps.scrollXBounds,
        nextProps.columns,
        nextProps.tableSettings
      )
    )
  ) {
    return false;
  }
  if (nextProps.rows.length !== prevProps.rows.length) {
    return false;
  }
  for (let i = 0; i < prevProps.rows.length; i++) {
    if (prevProps.rows[i].__address__ !== nextProps.rows[i].__address__) {
      return false;
    }
  }
  const propsAreEqual = makePropsAreEqual({
    name: 'WBTableGrid',
    deep: [
      'tempSelectedCount',
      'fixedColumns',
      'expandedRowAddresses',
      'columns',
      'tableSettings',
      // 'topLevelQueryVariables',
    ],
    // handled in shouldComponentUpdate
    ignore: ['scrollXBounds'],
    ignoreFunctions: true,
    debug: false,
    verbose: true,
  });
  return propsAreEqual(prevProps, nextProps);
});
